import { Card, Flex, Heading, Text } from '@applyboard/crystal-ui'
import { useQueryClient } from '@tanstack/react-query'
import { FileData } from '@applyboard/school-applications-types-lib'

import {
  RawApplicationAggregationResponse,
  RawApplicationsResponse,
  SchoolAdditionalDocument,
  useUpdateApplication,
} from '../../../hooks'
import {
  DecisionDocumentTags,
  DecisionType,
  DocumentTags,
  StudentDecisionResponse,
} from '../../../utils/enums'
import { getFilesOfTypeDecision } from '../ApplicationForms/utils'

import { ApplicationDetailCard } from './ApplicationDetailCard'
import { ApplicationDetailDecisionLetterHeading } from './ApplicationDetailDecisionLetterHeading'
import { ApplicationDetailAdditionalDocuments } from './ApplicationDetailAdditionalDocuments'
import { ApplicationDetailCardRejectDialogForm } from './ApplicationDetailCard/ApplicationDetailCardActions/ApplicationDetailCardRejectDialog'
import {
  Decision,
  DecisionResponseType,
} from '@applyboard/school-applications-types-lib/dist/src/backend/applications-api/resources/applications/student-decisions'
import { StudentApplication, StudentApplicationWithFiles } from '../types'
import { DocumentOutlineIcon } from '@applyboard/ui-icons'
import styled from '@emotion/styled'

type ApplicationDetailsProps = Readonly<{
  application: StudentApplication
  school: {
    additionalDocuments: SchoolAdditionalDocument[]
  } | null
  onOfferConditionsClick: () => void
}>

export type DecisionData = {
  type?: DecisionType
  hasOfferConditions: boolean
  documentTag: DecisionDocumentTags
  name: string
  url: string
  uploadedAt: Date | null
  isActiveRecord?: boolean
  decision?: Decision
}

function getTitleByType(type: DecisionDocumentTags): string {
  switch (type) {
    case DocumentTags.PRE_OFFER_LETTER:
      return 'Pre-Offer'
    case DocumentTags.FINAL_OFFER_LETTER:
      return 'Final Offer'
    case DocumentTags.WAITLIST_LETTER:
      return 'Waitlist'
    case DocumentTags.DECLINED_LETTER:
      return 'Declined'
    default:
      return '-'
  }
}

export function ApplicationDetails(props: ApplicationDetailsProps) {
  const queryClient = useQueryClient()

  const application = props.application

  const { isUpdatingApplication, updateApplication } = useUpdateApplication({
    id: props.application.id,
  })

  const studentDecisions = application?.attributes?.studentDecisions
  const hasOfferConditions = !!application?.attributes?.hasOpenDocumentRequests

  const onSuccess = (onSuccessProps: () => void) => (response: RawApplicationsResponse) => {
    queryClient.setQueryData(
      ['applications', response.data.id],
      (oldData: RawApplicationAggregationResponse): RawApplicationAggregationResponse => {
        return {
          data: {
            ...oldData.data,
            attributes: {
              ...oldData.data.attributes,
              application: response.data,
            },
          },
        }
      },
    )
    onSuccessProps()
  }

  const onSubmitAcceptOffer =
    (studentDecision: string | undefined) =>
    ({
      onError,
      onSuccess: onSuccessProps,
    }: {
      onSuccess: () => void
      onError: (err: Error) => void
    }): void => {
      if (!studentDecision) return
      updateApplication(
        {
          attributes: {
            studentDecisions: {
              [studentDecision]: {
                studentResponse: StudentDecisionResponse.ACCEPTED,
                decisionDate: new Date().toISOString(),
              },
            },
          },
        },
        {
          onSuccess: onSuccess(onSuccessProps),
          onError,
        },
      )
    }

  const onSubmitRejectOffer =
    (studentDecision: string) =>
    ({
      data,
      onError,
      onSuccess: onSuccessProps,
    }: {
      onSuccess: () => void
      onError: (err: Error) => void
      data: ApplicationDetailCardRejectDialogForm
    }): void => {
      updateApplication(
        {
          attributes: {
            studentDecisions: {
              [studentDecision]: {
                studentResponse: StudentDecisionResponse.REJECTED,
                decisionDate: new Date().toISOString(),
                reason: data.reason,
              },
            },
          },
        },
        {
          onSuccess: onSuccess(onSuccessProps),
          onError,
        },
      )
    }

  const getDecisionData = (): DecisionData[] => {
    const applicationFiles = (application as StudentApplicationWithFiles)?.attributes?.files
    const metaFiles = (application as StudentApplicationWithFiles)?.meta?.files
    const decisionFiles = getFilesOfTypeDecision(applicationFiles as FileData)

    const documentTagToDecisionType: Record<DecisionDocumentTags, DecisionType | undefined> = {
      [DocumentTags.PRE_OFFER_LETTER]: DecisionType.PRE_OFFER,
      [DocumentTags.WAITLIST_LETTER]: DecisionType.WAITLIST,
      [DocumentTags.FINAL_OFFER_LETTER]: DecisionType.FINAL_OFFER,
      [DocumentTags.DECLINED_LETTER]: undefined,
    }

    const decisions = Object.keys(decisionFiles).map((key): DecisionData => {
      const date = decisionFiles[key]?.uploadedAt
      const documentTag = decisionFiles[key]?.type as unknown as DecisionDocumentTags
      const type = documentTagToDecisionType[documentTag]
      return {
        name: decisionFiles[key]?.fileName ?? '',
        url: metaFiles[key]?.download?.url ?? '#',
        uploadedAt: date ? new Date(date) : null,
        documentTag: documentTag,
        isActiveRecord: decisionFiles[key]?.activeRecord,
        type,
        hasOfferConditions:
          documentTag === DocumentTags.DECLINED_LETTER ? false : hasOfferConditions,
        decision: type ? (studentDecisions?.[type] as Decision) : undefined,
      }
    })

    decisions.sort((a, b) => {
      // First priority: active records come before inactive ones
      const activeA = a.isActiveRecord !== false
      const activeB = b.isActiveRecord !== false

      if (activeA !== activeB) {
        return activeB ? 1 : -1 // Active records first
      }

      // Second priority: within the same active status, sort by upload date (newest first)
      if (a.uploadedAt === null) return -1
      if (b.uploadedAt === null) return 1
      return new Date(a.uploadedAt) > new Date(b.uploadedAt) ? -1 : 1
    })
    return decisions.filter(decision => decision.documentTag !== undefined)
  }

  return (
    <Flex direction="column" gap={6}>
      <Card>
        <Card.Header>
          <ApplicationDetailDecisionLetterHeading />
        </Card.Header>
        <Card.Divider mt={2} mb={3} />
        <Flex direction="column" gap={6}>
          {getDecisionData().map(decision => {
            const response = decision.decision?.studentResponse
            const isPendingPreOffer =
              decision.type === DecisionType.PRE_OFFER &&
              (response === DecisionResponseType.PENDING || !response)

            const showOfferConditions: boolean =
              (isPendingPreOffer ||
                decision.documentTag === DocumentTags.FINAL_OFFER_LETTER ||
                decision.documentTag === DocumentTags.WAITLIST_LETTER) &&
              decision.hasOfferConditions

            const isActiveRecord = decision.isActiveRecord

            return (
              <ApplicationDetailCard
                key={`studentDecision-${decision.documentTag}-${decision.uploadedAt?.getTime() || 'no-date'}`}
                title={getTitleByType(decision.documentTag)}
                status={response ?? undefined}
                issuedOn={decision.uploadedAt ?? undefined}
                hasOfferConditions={decision.hasOfferConditions}
                statusChangedOn={
                  decision.decision?.decisionDate
                    ? new Date(decision.decision?.decisionDate)
                    : undefined
                }
                file={decision}
                activeRecord={isActiveRecord}
                rejectDialog={
                  isPendingPreOffer && isActiveRecord && decision.type
                    ? {
                        onSubmit: onSubmitRejectOffer(decision.type),
                      }
                    : undefined
                }
                onOfferConditionsClick={
                  showOfferConditions ? props.onOfferConditionsClick : undefined
                }
                loading={isUpdatingApplication}
                acceptDialog={
                  isPendingPreOffer && isActiveRecord
                    ? {
                        programName: application?.attributes?.programSelected?.program?.name ?? '',
                        campusName: application?.attributes?.programSelected?.campus?.name ?? '',
                        intakeTermName:
                          application?.attributes?.programSelected?.programIntakeTerm?.name ?? '',
                        onSubmit: onSubmitAcceptOffer(decision.type),
                      }
                    : undefined
                }
              />
            )
          })}
        </Flex>
      </Card>

      <Card aria-label="Additional Documents Received">
        <Card.Header>
          <Flex gap={3}>
            <IconWrapper aria-hidden>
              <DocumentOutlineIcon />
            </IconWrapper>
            <Flex direction="column" gap={2}>
              <Heading level={2} variant="titleS">
                Additional Documents Received
              </Heading>
              <Text contrast="mid">
                The following documents have been shared by the school. Please download them, as
                they contain important information that will assist you in the next steps of the
                process.
              </Text>
            </Flex>
          </Flex>
        </Card.Header>
        <Card.Divider mt={2} mb={3} />
        <ApplicationDetailAdditionalDocuments files={props.school?.additionalDocuments} />
      </Card>
    </Flex>
  )
}

const IconWrapper = styled.span({
  display: 'flex',
  paddingTop: '4px',
})
